import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

/**
 * Pinned domain shortlist for the buyer-facing compare tray.
 *
 * Stored in localStorage so a buyer can pin candidates, bounce off to think,
 * come back the next day, and still see their shortlist waiting. Capped at
 * 3 pins because a side-by-side spec sheet stops being useful beyond that.
 *
 * `usePinnedDomains()` returns `{ pinned, isPinned, toggle, remove, clear, max }`
 * — every consumer subscribes to the same underlying storage via a custom
 * event so pinning on one component updates the tray in real time.
 */
const KEY = "cd_pinned_v1";
const MAX = 3;
const EVT = "cd:pinned-changed";

function readStorage() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(0, MAX) : [];
  } catch { return []; }
}
function writeStorage(next) {
  try {
    localStorage.setItem(KEY, JSON.stringify(next.slice(0, MAX)));
    window.dispatchEvent(new CustomEvent(EVT));
  } catch { /* ignore quota / private-mode errors */ }
}

/** Fire-and-forget lifecycle event to /api/track — used for pin/unpin/compare
 *  analytics so we can see which domains buyers shortlist most. */
function fireTrack(event, meta = {}) {
  try {
    const sid = localStorage.getItem("sniper_sid") || undefined;
    api.post("/track", { event, session_id: sid, meta }).catch(() => {});
  } catch { /* ignore */ }
}

export function usePinnedDomains() {
  const [pinned, setPinned] = useState(readStorage);

  useEffect(() => {
    const handler = () => setPinned(readStorage());
    window.addEventListener(EVT, handler);
    // Cross-tab sync — react to storage changes made by other tabs too.
    window.addEventListener("storage", (e) => { if (e.key === KEY) handler(); });
    return () => {
      window.removeEventListener(EVT, handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  const isPinned = useCallback(
    (domain) => pinned.some((d) => d.domain === domain),
    [pinned],
  );

  const toggle = useCallback((listing) => {
    if (!listing?.domain) return { ok: false, reason: "invalid" };
    const current = readStorage();
    const already = current.some((d) => d.domain === listing.domain);
    if (already) {
      writeStorage(current.filter((d) => d.domain !== listing.domain));
      fireTrack("compare.unpin", { domain: listing.domain, remaining: current.length - 1 });
      return { ok: true, action: "unpinned" };
    }
    if (current.length >= MAX) {
      fireTrack("compare.pin_max_reached", { domain: listing.domain });
      return { ok: false, reason: "max_reached" };
    }
    // Store the minimum viable snapshot — the tray + compare modal don't need
    // the full listing document.
    const snapshot = {
      domain: listing.domain,
      price: listing.price ?? null,
      currency: listing.currency || "USD",
      status: listing.status || null,
      pinned_at: new Date().toISOString(),
    };
    writeStorage([...current, snapshot]);
    fireTrack("compare.pin", {
      domain: listing.domain,
      price: listing.price ?? null,
      currency: listing.currency || "USD",
      total_pinned: current.length + 1,
    });
    return { ok: true, action: "pinned" };
  }, []);

  const remove = useCallback((domain) => {
    writeStorage(readStorage().filter((d) => d.domain !== domain));
    fireTrack("compare.unpin", { domain, remaining: readStorage().length });
  }, []);

  const clear = useCallback(() => {
    const domains = readStorage().map((d) => d.domain);
    writeStorage([]);
    fireTrack("compare.clear", { count: domains.length, domains });
  }, []);

  return { pinned, isPinned, toggle, remove, clear, max: MAX };
}

/** Exposed so the tray can log "opened compare modal" alongside pin events. */
export function trackCompareEvent(event, meta) {
  fireTrack(event, meta || {});
}
